<template>
  <v-app>
    <!-- 세련된 네비게이션 바 -->
    <v-app-bar
      v-if="!isApplyLayout"
      class="main-navbar"
      :class="{ 'navbar-scrolled': scrolled }"
      elevation="0"
      height="70"
      flat
      fixed
      style="transition: none !important; transform: none !important;"
    >
      <!-- 평창군 로고 -->
      <div
        @click="$router.push('/')"
        class="logo-btn d-flex align-center ml-3"
        style="cursor: pointer;"
      >
        <img
          :src="versionedImage('/images/logo.png')"
          alt="평창군 로고"
          height="50"
          class="mr-3"
        >
        <div class="logo-text d-none d-md-block">청소년 꿈키움카드</div>
      </div>

      <v-spacer></v-spacer>

      <!-- 데스크톱 메뉴 -->
      <div class="d-none d-md-flex align-center">
        <v-btn
          v-for="item in menuItems"
          :key="item.path"
          variant="text"
          class="mx-2 nav-menu-item"
          :class="{ 'nav-menu-active': isActiveRoute(item.path) }"
          @click="$router.push(item.path)"
          rounded
        >
          {{ item.title }}
        </v-btn>
      </div>

      <!-- 모바일 메뉴 버튼 -->
      <v-btn
        icon
        class="d-md-none mobile-menu-btn"
        @click="drawer = !drawer"
        color="primary"
        variant="tonal"
        style="transition: none !important; transform: none !important;"
      >
        <v-icon>mdi-menu</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- 모바일 네비게이션 드로어 -->
    <v-navigation-drawer
      v-model="drawer"
      temporary
      location="right"
      width="300"
      class="mobile-drawer"
      style="z-index: 2002 !important;"
    >
      <div class="drawer-header pa-4 bg-primary">
        <div class="d-flex align-center">
          <img 
            :src="versionedImage('/images/logo.png')" 
            alt="평창군 로고" 
            height="40"
            class="mr-3"
            style="filter: brightness(0) invert(1);"
          >
          <div>
            <div class="text-h6 font-weight-bold text-white">청소년 꿈키움카드</div>
            <div class="text-caption text-white-50">평창군청</div>
          </div>
        </div>
      </div>

      <v-list nav class="py-2">
        <v-list-item
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          @click="drawer = false"
          rounded
          class="mx-2 my-1"
        >
          <template v-slot:prepend>
            <v-icon :color="item.color || 'grey-darken-1'">{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>

      </v-list>

      <template v-slot:append>
        <div class="pa-4">
          <v-btn
            block
            color="primary"
            variant="tonal"
            rounded
            href="https://www.pc.go.kr/"
            target="_blank"
          >
            <v-icon start>mdi-open-in-new</v-icon>
            평창군청 홈페이지
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- 메인 콘텐츠 -->
    <v-main class="main-content has-navbar">
      <router-view v-slot="{ Component }">
        <transition name="page-transition" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </v-main>

    <!-- 푸터 -->
    <v-footer 
      v-if="!isApplyLayout"
      class="modern-footer"
      color="grey-darken-4"
    >
      <v-container>
        <v-row align="center" class="py-2">
          <!-- 왼쪽: 개인정보처리방침, 이용문의 -->
          <v-col cols="12" md="8" class="text-left">
            <div class="d-flex align-center mb-2">
              <v-btn
                variant="text"
                color="white"
                size="small"
                @click="$router.push('/privacy')"
                class="px-1 footer-privacy-btn"
              >
                개인정보처리방침
              </v-btn>
              <span class="text-caption text-grey mx-2">|</span>
              <span class="text-caption text-grey-lighten-1">이용문의</span>
            </div>
            <div class="text-caption text-grey-lighten-2">
              바우처서비스(주) &nbsp;|&nbsp; 고객센터 : <span class="text-white">1588-3259</span>
            </div>
            <div class="text-caption text-grey-lighten-3 mt-1">
              @바우처서비스(주) All Right Reserved.
            </div>
          </v-col>
          
          <!-- 오른쪽: 평창군 로고 -->
          <v-col cols="12" md="4" class="text-center text-md-right">
            <a 
              href="https://www.pc.go.kr/" 
              target="_blank"
              class="footer-logo-link"
            >
              <img 
                :src="versionedImage('/images/logo.png')" 
                alt="평창군" 
                height="30"
                style="opacity: 0.5; filter: brightness(0) invert(1);"
                class="footer-logo"
              >
            </a>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
    
    <!-- iOS 디바이스를 위한 오류 경고창 -->
    <v-snackbar
      v-model="showErrorNotice"
      :timeout="10000"
      color="warning"
      rounded="pill"
      location="top"
      class="mt-16"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-alert-circle</v-icon>
        <span>iOS 기기에서 일부 기능이 제한될 수 있습니다.</span>
        <v-btn
          variant="text"
          size="small"
          @click="showErrorNotice = false"
          class="ml-auto"
        >
          닫기
        </v-btn>
      </div>
    </v-snackbar>

    <!-- 전역 알림 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      location="top"
      class="mt-16"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          닫기
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { versionedImage } from '@/utils/imageHelper'
import { snackbar } from '@/utils/snackbar'

export default {
  name: 'App',
  setup() {
    const route = useRoute()
    const drawer = ref(false)
    const scrolled = ref(false)
    const isIOSDevice = ref(false)
    const showErrorNotice = ref(false)

    const menuItems = ref([
      { title: '홈', path: '/', icon: 'mdi-home', color: 'primary' },
      { title: '카드소개', path: '/intro', icon: 'mdi-card-account-details' },
      { title: '가맹점', path: '/shops', icon: 'mdi-store' },
      { title: '잔액조회', path: '/balance', icon: 'mdi-credit-card-search' },
      { title: '공지사항', path: '/notice', icon: 'mdi-bulletin-board' },
      { title: '고객센터', path: '/support', icon: 'mdi-help-circle' }
    ])
    
    const isApplyLayout = computed(() => {
      // 모든 페이지에서 동일한 헤더/푸터 사용
      return false
    })
    
    const isActiveRoute = (path) => {
      if (path === '/') {
        return route.path === '/'
      }
      return route.path.startsWith(path)
    }
    
    const handleScroll = () => {
      scrolled.value = window.scrollY > 50
    }
    
    onMounted(() => {
      window.addEventListener('scroll', handleScroll)

      // iOS 기기 감지
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

      if (isIOS || isSafari) {
        isIOSDevice.value = true
        document.body.classList.add('ios-device')
      }
    })
    
    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)    })
    
    return {
      drawer,
      scrolled,
      menuItems,
      isApplyLayout,
      isIOSDevice,
      showErrorNotice,
      isActiveRoute,
      snackbar,
      versionedImage
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700;900&display=swap');

/* 글로벌 스타일 */
:root {
  --primary-gradient: linear-gradient(135deg, #171c61 0%, #2d3692 100%);
  --secondary-gradient: linear-gradient(135deg, #5a63c4 0%, #171c61 100%);
  --light-gradient: linear-gradient(135deg, #f5f6fb 0%, #e8eaf7 100%);
}

html, body {
  overflow-x: hidden !important;
  overflow-y: scroll !important;
  height: auto !important;
  min-height: 100vh;
}

body {
  font-family: 'Noto Sans KR', sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.v-application {
  overflow: visible !important;
  height: auto !important;
  min-height: 100vh !important;
}

.v-application__wrap {
  min-height: 100vh !important;
  height: auto !important;
}

.v-main {
  overflow: visible !important;
  height: auto !important;
}

/* 네비게이션 바 스타일 */
.main-navbar {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: none !important;
  z-index: 2000 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100vw !important;
  max-width: 100vw !important;
  transform: none !important;
  box-shadow: none !important;
}

.main-navbar :deep(.v-toolbar__content) {
  width: 100% !important;
  max-width: none !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
  transition: none !important;
  transform: none !important;
}

.main-navbar :deep(.v-toolbar__prepend),
.main-navbar :deep(.v-toolbar__append) {
  transition: none !important;
  transform: none !important;
}

.main-navbar.navbar-scrolled {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(30px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
  transition: none !important;
}

.main-navbar.navbar-scrolled :deep(.v-toolbar__content) {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
}

.logo-btn {
  transition: transform 0.3s ease;
  opacity: 1 !important;
}

.logo-btn:hover {
  transform: scale(1.05);
}

.logo-btn img {
  opacity: 1 !important;
}

.logo-text {
  color: #171c61;
  font-weight: 700 !important;
  font-size: 1.1rem;
  white-space: nowrap;
  transition: all 0.3s ease;
  position: relative;
}

.logo-text::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #171c61, #2d3692);
  transition: width 0.3s ease;
}

.logo-btn:hover .logo-text::after {
  width: 100%;
}

.nav-menu-item {
  font-weight: 600 !important;
  color: #424242 !important;
  transition: all 0.3s ease;
  position: relative;
}

.nav-menu-item.nav-menu-active {
  color: #171c61 !important;
}

.nav-menu-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: var(--primary-gradient);
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-menu-item:hover {
  color: #171c61 !important;
  transform: translateY(-2px) !important;
}

.nav-menu-item:hover::after {
  width: 80%;
}

/* 모바일 메뉴 버튼 */
.mobile-menu-btn {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  z-index: 2001 !important;
  position: relative !important;
  transition: none !important;
  transform: none !important;
}

.mobile-menu-btn:hover {
  box-shadow: 0 4px 12px rgba(23, 28, 97, 0.4) !important;
  transform: none !important;
}

/* 모바일 드로어 */
.mobile-drawer {
  z-index: 2002 !important;
}

.mobile-drawer .drawer-header {
  background: var(--primary-gradient);
}

/* 메인 콘텐츠 */
.main-content {
  background: linear-gradient(180deg, #FFFFFF 0%, #f5f6fb 100%);
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
}

.has-navbar {
  padding-top: 70px !important;
  margin-top: 0 !important;
}

.has-navbar > * {
  position: relative;
  max-width: 100vw;
}

/* 푸터 스타일 */
.modern-footer {
  background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%) !important;
  padding: 20px 0 12px !important;
}

.modern-footer :deep(.v-container) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.modern-footer :deep(.v-row) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.footer-logo-link {
  transition: opacity 0.3s ease;
}

.footer-logo-link:hover .footer-logo {
  opacity: 0.8 !important;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.footer-links a {
  color: #e0e0e0;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  position: relative;
  padding-left: 12px;
}

.footer-links a::before {
  content: '›';
  position: absolute;
  left: 0;
  color: #8a91d8;
  transition: transform 0.3s ease;
}

.footer-links a:hover {
  color: #8a91d8;
  transform: translateX(4px);
}

/* 푸터 개인정보처리방침 버튼 */
.footer-privacy-btn {
  font-weight: 600 !important;
}

.footer-privacy-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.footer-links a:hover::before {
  transform: translateX(4px);
}

/* 페이지 트랜지션 */
.page-transition-enter-active,
.page-transition-leave-active {
  transition: all 0.3s ease;
}

.page-transition-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-transition-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* 스크롤바 스타일 */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #171c61, #2d3692);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #2d3692, #1a1f54);
}

/* 버튼 호버 효과 */
.v-btn {
  transition: all 0.3s ease !important;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(23, 28, 97, 0.3) !important;
}

/* 네비게이션 바 내부 버튼은 transform 없음 */
.main-navbar .v-btn {
  transition: none !important;
  transform: none !important;
}

.main-navbar .v-btn:hover {
  transform: none !important;
}

/* 카드 효과 */
.v-card {
  transition: all 0.3s ease !important;
}

.v-card:hover {
  transform: translateY(-4px);
}

/* iOS 기기 최적화 */
.ios-device .v-application {
  -webkit-tap-highlight-color: transparent;
}

.ios-device .v-btn {
  -webkit-appearance: none;
  appearance: none;
}

/* 반응형 디자인 개선 */
@media (max-width: 960px) {
  .main-navbar {
    height: 60px !important;
  }

  .has-navbar {
    padding-top: 60px !important;
  }

  .modern-footer {
    padding: 20px 0 12px !important;
  }
}

@media (max-width: 600px) {
  .logo-text {
    font-size: 1rem !important;
  }
  
  .footer-links {
    gap: 8px;
  }
}

/* 애니메이션 */
@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@keyframes gradient-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.animate-pulse {
  animation: pulse 2s infinite;
}

.gradient-animated {
  background-size: 200% 200%;
  animation: gradient-shift 15s ease infinite;
}

</style>